import { useState, useEffect } from 'react';

interface CookieConsentProps {
  language?: 'lt' | 'en';
  onPolicyClick?: (policyType: 'privacy' | 'cookies') => void;
}

export default function CookieConsent({ language = 'lt', onPolicyClick }: CookieConsentProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Delay showing the banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    setIsVisible(false);
  };

  const handlePolicyClick = (policyType: 'privacy' | 'cookies') => {
    // Dispatch custom event to open policy modal
    const event = new CustomEvent('openPolicy', {
      detail: { policyType }
    });
    window.dispatchEvent(event);
    
    if (onPolicyClick) {
      onPolicyClick(policyType);
    }
  };

  if (!isVisible) return null;

  const content = {
    lt: {
      message: 'Šioje svetainėje naudojami slapukai, kad užtikrintume geriausią patirtį.',
      privacy: 'Privatumo politika',
      cookies: 'Slapukų politika',
      accept: 'Sutinku',
      decline: 'Nesutinku'
    },
    en: {
      message: 'This website uses cookies to ensure you get the best experience.',
      privacy: 'Privacy Policy',
      cookies: 'Cookie Policy',
      accept: 'Accept',
      decline: 'Decline'
    }
  };

  const t = content[language];

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-500 ease-out ${
        isVisible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="bg-stone-900 text-white px-4 py-4 shadow-lg">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex-1 text-center sm:text-left">
            <p className="text-sm text-stone-200">
              {t.message}{' '}
              <button 
                onClick={() => handlePolicyClick('privacy')}
                className="underline hover:text-amber-400 transition-colors cursor-pointer"
              >
                {t.privacy}
              </button>
              {' | '}
              <button 
                onClick={() => handlePolicyClick('cookies')}
                className="underline hover:text-amber-400 transition-colors cursor-pointer"
              >
                {t.cookies}
              </button>
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDecline}
              className="px-4 py-2 text-sm text-stone-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              {t.decline}
            </button>
            <button
              onClick={handleAccept}
              className="px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {t.accept}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
